import React, { useRef } from 'react';
import DOMPurify from 'dompurify';
import { Button, Select, Space, Tag, Typography } from 'antd';
import { routeHistory } from '@nackle/biw-web-router';
import { EditOutlined } from '@ant-design/icons';
import { Routes } from '../../../routes';
import DeleteButton from '../../components/DeleteButton/DeleteButton';
import LinksList from '../../components/LinksList/LinksList';
import { renderWithSearchWords } from '../../components/SearchBar/SearchBar';
import useDeleteLearningObject from '../../gql/hooks/useDeleteLearningObject'; 
import SearchQueryList from '../../components/SearchQueryList/SearchQueryList';

let expandedRows = [];

const LearningObjectsListPage = () => { 
    const listPageRef = useRef( null );

    const { deleteLearningObject, isDeleting } = useDeleteLearningObject();
    
    const breadcrumbs = [
        { name: 'Course Studio', path: Routes.Home.buildPath() },
        { name: 'Learning Content', path: Routes.LearningContentNav.buildPath() },
        { name: 'Learning Objects' }
    ];

    const uiStrings = {
        cardTitle: 'Learning Objects',
        createButtonTitle: 'Create New Learning Object'
    };

    const handleDeleteClick = async ( id ) => {
        await deleteLearningObject( id, () => {
            listPageRef.current.reload();
        } );
    };

    const handleCreateClick = () => {
        routeHistory.push( Routes.CreateObject.buildPath() );
    };

    const handleSetExpandedRows = ( rows ) => {
        expandedRows = rows;
    };

    const renderExpandedRow = ( object ) => {
        return (
            <div className="row-expand">
                <div className="flex-container">
                    <div className="flex-left">
                        <strong>Content</strong><br/>
    
                        { ( !object.content && !object.name ) && 
                            <p className="no-translation">No translation for this language</p>
                        }
                                                        
                        { ( object.content || object.name ) && 
                        <Typography.Paragraph ellipsis={ { rows: 6 } }>
                            <span dangerouslySetInnerHTML={ { __html: DOMPurify.sanitize( object.content ) } } /> 
                        </Typography.Paragraph>
                        }
                    </div>
    
                    <div className="flex-right">
                        <Select
                            bordered={ false }
                            dropdownMatchSelectWidth={ 200 }
                            defaultValue={ object._selectedLocale }
                            onChange={ ( value ) => listPageRef.current.handleLocaleChange( value, object ) }
                            options={ 
                                object.locales.map( l => (
                                    {
                                        key: l.code,
                                        value: l.code,
                                        label: listPageRef.current.getLabelForLocale( l.code )
                                    }
                                ) )
                            }
                            size="small"
                        />
                    </div>
                </div>

                { /* Used by courses list */ }
                <LinksList 
                    title="Courses using this Learning Object" 
                    items={ object.usedByCourses } 
                    handleClickCallback= { ( id ) => routeHistory.push( Routes.EditCourse.buildPath( id ) ) } 
                />
            </div>
        );
    };

    const tableColumns = [
        {
            dataIndex: 'name',
            key: 'name',
            title: 'Name',
            sorter: ( a, b ) => 0,
            sortKey: 'name',
            width: 350,
            fixed: 'left',
            render: ( name, i ) => {
                if ( !i.content && !i.name ) {
                    return <span className="no-translation">No translation for this language</span>;
                } else {
                    return (
                        <span className="name-column">
                            { renderWithSearchWords( name, listPageRef.current.getSearchWords() ) }
                        </span>
                    );
                }
            }
        },
        {
            dataIndex: 'format',
            key: 'format',
            title: 'Format',
            fixed: 'left',
            render: ( format ) => (
                <Tag className="uppercase" color="blue">{ format }</Tag>
            )
        },
        {
            dataIndex: 'courseCount',
            key: 'courseCount', 
            title: 'Used By',
            render: ( courseCount, i ) => courseCount > 0 ? `${ i.courseCount } Course(s)` : null
        },
        {
            dataIndex: 'createdAt',
            key: 'createdAt', 
            title: 'Created At',
            sorter: ( a, b ) => 0,
            sortKey: 'createdAt',
            render: ( createdAt ) => <span>{ createdAt }</span>
        },
        {
            dataIndex: 'updatedAt',
            key: 'updatedAt', 
            title: 'Updated At',
            sorter: ( a, b ) => 0,
            sortKey: 'updatedAt',
            render: ( updatedAt ) => <span>{ updatedAt }</span>
        },
        {
            title: 'Actions',
            key: 'actions', 
            fixed: 'right', 
            width: '100px',
            render: ( item ) => {
                return (
                    <>
                        <Space>
                            <Button 
                                type="link" 
                                icon={ <EditOutlined /> } 
                                onClick= { () => routeHistory.push( Routes.EditObject.buildPath( item.id ) ) }
                            />
                            
                            <DeleteButton
                                id={ item.id }   
                                title={ 'Confirm Delete' }
                                disabled={ item.courseCount >  0 }
                                deleteCallback={ ( i ) => handleDeleteClick( i ) }
                                confirmMessage= { 
                                    <>
                                        <p>Are you <i>sure</i> you want to permanently delete the learning object <b>{ item.name }</b>? 
                                        This action cannot be undone.</p>
                                    </>
                                }
                            />
                        </Space>
                    </> 
                );
            }   
        }
    ]; 
  
    return (
        <SearchQueryList 
            ref={ listPageRef }
            type="objects" 
            uiStrings={ uiStrings }
            breadcrumbs={ breadcrumbs }
            tableColumns={ tableColumns }
            isDeleting={ isDeleting }
            renderExpandedRowCallback= { renderExpandedRow }
            createClickCallback={ handleCreateClick }
            setExpandedRowsCallback= { handleSetExpandedRows }
            defaultExpandedRows= { expandedRows }
        />
    );
};

export default LearningObjectsListPage;
